import React, { useRef, useState } from 'react';
import Chart, { ChartConfiguration } from 'chart.js';
import { ChartComponent } from '../../general/ChartComponent';
import { ForecastValue } from '../../../api/robotapi';
import { merge } from 'lodash';
import { toPrice } from '../../../utils/parsers';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { COLORS } from './../../../config/constants';
import { ForecastChartTooltip } from './ForecastChartTooltip';
import { useResizeObserver, DOMRectReadOnly } from '../../../hooks/useResizeObserver';
import { useTranslation } from 'react-i18next';

Chart.defaults.global.defaultFontFamily = 'museo-sans, sans-serif';
Chart.defaults.global.defaultFontColor = COLORS.bndGrey;
Chart.defaults.global.defaultFontSize = 16;

// extract ChartDataSet interface from chartjs-plugin-datalabels, which merges extra options
type ChartDataSet = Context['dataset'];

export type ForecastChartProperties = {
    values: ForecastValue[];
    type: 'line' | 'bar';
    goal: number;
    inYears: number;
    deposit: number;
};


export const ForecastChart: React.FC<ForecastChartProperties> = ({ values, type = 'bar', inYears, goal, deposit }) => {
    // tooltip
    const [hoverValue, setHoverValue] = useState<keyof Omit<ForecastValue, 'date'>>('median');
    const currentValue = values[values.length - 1]?.[hoverValue] || 0;
    const tooltipRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<HTMLDivElement>(null);
    const size = useResizeObserver({ ref: chartRef });
    const { t } = useTranslation('investment');

    const tooltipOptions = getTooltipOptions(size, tooltipRef.current, setHoverValue);

    let data: ChartConfiguration['data'] = { datasets: toDataSets(values, type, deposit, goal) };
    let options: ChartConfiguration['options'] = Object.assign({}, chartOptions, tooltipOptions);

    const labels = ['', 
        t("forecast-screen-result-pessimistic-market"),
        t("forecast-screen-result-expected-market"),
        t("forecast-screen-result-optimistic-market"),
        ''
    ];
    const forecastConfig = { type: 'line', plugins: [ChartDataLabels], data: { labels } };
    const config = merge({}, forecastConfig, { data, options });

    return (
        <>
            <div ref={chartRef} className="w-full h-full" style={{ minHeight: '400px' }}>
                <ChartComponent config={config} />
            </div>
            <ForecastChartTooltip
                ref={tooltipRef}
                currentValue={currentValue}
                goal={goal}
                deposit={deposit}
                inYears={inYears}
            />
        </>
    );
};

/** Parses ForecastValues to Chartjs Datasets */
const toDataSets = (
    forecastValues: ForecastValue[],
    forecastType: ForecastChartProperties['type'],
    initialDeposit: number,
    goal: number,
): ChartDataSet[] => {
    const { pessimistic, optimistic, median } =
        forecastValues.length > 0
            ? forecastValues[forecastValues.length - 1]
            : { pessimistic: 0, optimistic: 0, median: 0 };
    const goalIsZero = goal === 0;
    return [
        {
            type: 'bar',
            data: [0, pessimistic, median, optimistic, 0],
            backgroundColor: [
                COLORS.bndBarChartBlue,
                COLORS.bndBarChartBlue,
                COLORS.bndBarChartBlue,
                COLORS.bndBarChartBlue,
                COLORS.bndBarChartBlue,
            ],
            borderWidth: 0,
            maxBarThickness: 85,
            datalabels: {
                align: 'end',
                anchor: 'end',
                color: COLORS.bndBlack,
                font: function (context) {
                    return {
                        size: 16,
                        weight: 'bold',
                    };
                },
                formatter: function (value, context) {
                    return toPrice(value, { onlyFullEuros: true, withCurrencySymbol: true });
                },
                display: function (context) {
                    const data = context.chart.data.datasets?.[context.datasetIndex].data;
                    if (context.dataIndex === 0 || (data && context.dataIndex === data.length - 1)) {
                        return false;
                    }
                    return true;
                },
            },
        },
        {
            type: 'line',
            data: [initialDeposit, initialDeposit, initialDeposit, initialDeposit, initialDeposit],
            fill: true,
            backgroundColor: COLORS.bndPinkLight,
            borderColor: COLORS.bndPink,
            hitRadius: 0,
            datalabels: {
                align: 'right',
                offset: 1,
                anchor: 'center',
                color: COLORS.bndWhite,
                backgroundColor: COLORS.bndPink,
                borderRadius: 6,
                padding: {
                    top: 4,
                    bottom: 4,
                    left: 8,
                    right: 8,
                },
                font: function (context) {
                    return {
                        size: 14,
                        weight: 'bold',
                    };
                },
                formatter: function (value, context) {
                    return `Inleg: ${toPrice(value, { onlyFullEuros: true, withCurrencySymbol: true })}`;
                },
                display: function (context) {
                    return context.dataIndex === 0;
                },
            },
        },
        {
            type: 'line',
            data: [goal, goal, goal, goal, goal],
            fill: false,
            borderColor: COLORS.bndGreen,
            hitRadius: 0,
            showLine: !goalIsZero,
            datalabels: {
                align: 'right',
                offset: 1,
                anchor: 'center',
                color: COLORS.bndWhite,
                backgroundColor: COLORS.bndGreenDark,
                borderRadius: 6,
                padding: {
                    top: 4,
                    bottom: 4,
                    left: 8,
                    right: 8,
                },
                font: function (context) {
                    return {
                        size: 14,
                        weight: 'bold',
                    };
                },
                formatter: function (value, context) {
                    return `Doel: ${toPrice(value, { onlyFullEuros: true, withCurrencySymbol: true })}`;
                },
                display: function (context) {
                    return context.dataIndex === 0 && !goalIsZero;
                },
            },
        },
    ];
};

const chartOptions: ChartConfiguration['options'] = {
    layout: {
        padding: {
            top: 24,
        },
    },
    responsive: true,
    maintainAspectRatio: false,
    legend: {
        display: false,
    },
    scales: {
        yAxes: [
            {
                position: 'right',
                gridLines: {
                    color: 'rgba(36, 36, 36, 0.6)',
                    lineWidth: 1.2,
                    drawBorder: false,
                    drawTicks: false,
                },
                ticks: {
                    padding: 8,
                    beginAtZero: true,
                    callback: function (value, index, values) {
                        return toPrice(+value, { onlyFullEuros: true, withCurrencySymbol: true });
                    },
                },
            },
        ],
        xAxes: [
            {
                stacked: true,
                gridLines: {
                    display: false,
                },
                ticks: {
                    maxRotation: 45,
                },
            },
        ],
    },
    elements: {
        line: {
            tension: 0,
            borderWidth: 2,
            borderDash: [8, 12],
        },
        point: {
            radius: 0,
            hoverRadius: 0,
            hoverBorderWidth: 0,
            borderWidth: 0,
            hitRadius: 0,
        },
    },
};

const getTooltipOptions = (
    canvasRect: DOMRectReadOnly | null,
    tooltipElement: HTMLDivElement | null,
    setHoverValue: React.Dispatch<React.SetStateAction<'pessimistic' | 'median' | 'optimistic'>>,
): ChartConfiguration['options'] => ({
    tooltips: {
        enabled: false,
        mode: 'x',
        position: 'average',
        custom: function (tooltip) {
            if (tooltip.dataPoints?.[0]?.index !== undefined)
                setHoverValue(
                    tooltip.dataPoints?.[0]?.index === 1
                        ? 'pessimistic'
                        : tooltip.dataPoints?.[0]?.index === 2
                        ? 'median'
                        : tooltip.dataPoints?.[0]?.index === 3
                        ? 'optimistic'
                        : 'median',
                );

            if (tooltipElement && canvasRect) {
                tooltipElement.hidden = false;
                if (tooltip.opacity === 0) {
                    tooltipElement.classList.replace('opacity-100', 'opacity-0');
                } else {
                    tooltipElement.classList.replace('opacity-0', 'opacity-100');
                }

                tooltipElement.style.left =
                    ((tooltip.caretX - tooltipElement.clientWidth / 2) / canvasRect.width) * 100 + '%';
                tooltipElement.style.top = (tooltip.caretY / canvasRect.width) * 100 + '%';
            }
        },
        intersect: true,
    },
});
